import React, { useEffect, useMemo, useState } from "react";
import { selectResidentOrders } from "./residentOrders";
import Icon from "../shared/Icon";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import {
  fetchAllOrders,
  fetchOrderDetail,
  fetchResidents,
  isAbortError,
  removeResident,
  updateResident,
  OPEN_STATES,
} from "../../../services/managerApi";
import { splitFullName } from "../Etiketten/residentsSource";
import { openReceipts } from "../shared/receipts";
import { MANAGER_ROUTES } from "../../../utils/managerRouting";
import OrderDrawer from "../OrderDrawer";
import StatusPill from "../shared/StatusPill";
import { EmptyState, ErrorState, LoadingCard } from "../shared/States";
import {
  formatCurrency,
  formatKilos,
  formatMonthYear,
  formatShortDate,
  initialsOf,
} from "../shared/format";

const GRID = "1fr 1fr 1fr 1.15fr .6fr .6fr .9fr 46px";

const Bewonersfiche = ({ t, authUser, match, history }) => {
  const residentId = match?.params?.residentId;
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [receiptError, setReceiptError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [nameForm, setNameForm] = useState({ firstName: "", lastName: "" });
  const [nameOverride, setNameOverride] = useState(null);
  const [saveState, setSaveState] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [removeState, setRemoveState] = useState(null);
  const [idFromOrder, setIdFromOrder] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Même paire que la liste : l'annuaire identifie le résident, les
        // commandes racontent son activité. Sans le premier, un résident sans
        // commande n'existait pas et sa fiche répondait « introuvable ».
        const [result, roster] = await Promise.all([
          fetchAllOrders({ authUser, signal: controller.signal }),
          fetchResidents({ authUser, signal: controller.signal }).catch(() => []),
        ]);
        if (!active) return;
        setOrders(result.orders);
        setDirectory(roster);
      } catch (err) {
        if (!active || isAbortError(err)) return;
        setError(err);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [authUser, reloadKey]);

  // Même règle de rattachement que la liste, annuaire compris — voir
  // `residentOrders.js`. La comparaison directe qui vivait ici ne consultait
  // pas l'annuaire, donc la fiche contredisait la ligne depuis laquelle on
  // venait de cliquer : « 3 commandes » dans la liste, « 0 bestellingen » et un
  // historique vide sur la fiche.
  const residentOrders = useMemo(
    () => selectResidentOrders(orders, directory, residentId),
    [orders, directory, residentId]
  );

  /**
   * Identité du résident. Les commandes en portent une, mais un résident qui
   * n'en a aucune n'existe que dans l'annuaire — c'est le cas de celui qu'on
   * vient de créer. La fiche s'ouvre alors avec un historique vide, ce qui est
   * la vérité, plutôt qu'un « introuvable » qui est faux.
   */
  const resident = useMemo(() => {
    if (residentOrders[0]) return residentOrders[0];

    const entry = directory.find(
      (candidate) =>
        String(candidate.id) === String(residentId) ||
        String(candidate.woshUserId) === String(residentId)
    );
    if (!entry) return null;

    return {
      residentId: entry.id,
      woshUserId: entry.woshUserId,
      residentName: entry.name,
      room: null,
    };
  }, [residentOrders, directory, residentId]);
  /**
   * Identifiant à envoyer au serveur pour toute écriture.
   *
   * La route peut porter l'identifiant WOSH — « M3521 » — quand les commandes
   * ne sérialisent pas `user_id`, et c'est le cas de `/customer/managers`.
   * Envoyer cette chaîne à `PUT`/`DELETE /residents/:id` produit un 403 : le
   * serveur ne trouve personne, donc rien qui appartienne au manager.
   * L'annuaire, lui, donne l'identifiant numérique.
   */
  /**
   * Identifiant envoyé au serveur pour les écritures.
   *
   * Le numérique est préféré — c'est ce qu'attend une route Rails classique —
   * mais à défaut on envoie l'identifiant WOSH plutôt que rien. Refuser
   * d'essayer reposait sur une hypothèse jamais vérifiée : les deux 403 qui
   * l'avaient motivée venaient de résidents avec une commande en cours, donc du
   * verrou métier, pas de l'identifiant. C'est au serveur de trancher.
   */
  const writeId = useMemo(() => {
    const entry = directory.find(
      (candidate) =>
        String(candidate.id) === String(residentId) ||
        String(candidate.woshUserId) === String(residentId)
    );

    const numeric = [entry?.id, resident?.residentId, idFromOrder].find(
      (value) => value != null && /^\d+$/.test(String(value))
    );
    return numeric ?? residentId;
  }, [directory, resident, idFromOrder, residentId]);

  const current = residentOrders.find((order) => OPEN_STATES.includes(order.state));

  /**
   * Le serveur refuse de modifier un résident dont une commande est en cours,
   * et répond un « Access Denied » qui n'explique rien. On connaît ses
   * commandes : autant désactiver les actions et dire pourquoi avant le clic,
   * plutôt que de laisser partir une requête dont on sait qu'elle échouera.
   */
  const lockedByOrder = Boolean(current);
  // Toutes les commandes, pas seulement les terminées : un résident dont tout
  // est en cours affichait un historique vide alors qu'il a des commandes.
  const historyOrders = residentOrders;

  /**
   * Dernier recours pour l'identifiant numérique.
   *
   * Ni la liste des commandes ni l'annuaire ne le donnent pour certains
   * résidents. Le détail d'une commande peut le porter : une requête, une
   * seule, et seulement quand tout le reste a échoué.
   */
  useEffect(() => {
    const alreadyNumeric = /^\d+$/.test(String(writeId));
    if (alreadyNumeric || !residentOrders.length) return;
    let active = true;

    fetchOrderDetail({ authUser, orderId: residentOrders[0].id })
      .then((detail) => {
        if (active && detail?.residentId != null) setIdFromOrder(detail.residentId);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [writeId, residentOrders, authUser]);

  const totals = useMemo(() => {
    const now = new Date();
    const thisMonth = residentOrders.filter(
      (order) =>
        order.pickupDate &&
        order.pickupDate.getFullYear() === now.getFullYear() &&
        order.pickupDate.getMonth() === now.getMonth()
    );
    const all = residentOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const weeks = Math.max(1, residentOrders.length / 2);

    return {
      month: thisMonth.reduce((sum, order) => sum + (order.total || 0), 0),
      all,
      perWeek: all / weeks,
      kilos: residentOrders.reduce((sum, order) => sum + (order.kilos || 0), 0),
    };
  }, [residentOrders]);

  const displayName = nameOverride || resident?.residentName || "";

  const startEditing = () => {
    setSaveState(null);
    setNameForm(splitFullName(displayName));
    setIsEditing(true);
  };

  const saveName = async () => {
    setSaveState({ status: "saving" });
    const result = await updateResident({
      authUser,
      residentId: writeId,
      firstName: nameForm.firstName,
      lastName: nameForm.lastName,
    });

    if (result.ok) {
      // La liste vient des commandes : elle ne reflétera le nouveau nom qu'au
      // prochain rechargement. On l'affiche localement en attendant.
      setNameOverride([nameForm.firstName, nameForm.lastName].filter(Boolean).join(" "));
      setIsEditing(false);
      setSaveState({ status: "saved" });
      return;
    }
    setSaveState({ status: "error", reason: result.reason, detail: result.detail });
  };

  /**
   * Retire le résident du point de collecte.
   *
   * Confirmation obligatoire, et depuis la fiche seulement : sur la liste, un
   * clic mal placé retirerait quelqu'un sans qu'on ait vu de qui il s'agit.
   */
  const remove = async () => {
    setRemoveState({ status: "removing" });
    const result = await removeResident({ authUser, residentId: writeId });

    if (result.ok) {
      history.push(MANAGER_ROUTES.residents);
      return;
    }
    setConfirmRemoval(false);
    setRemoveState({ status: "error", reason: result.reason, detail: result.detail });
  };

  const downloadReceipts = async (ids) => {
    setReceiptError(null);
    const result = await openReceipts({ authUser, orderIds: ids });
    if (!result.ok) setReceiptError(result.detail || result.reason);
  };

  if (isLoading) {
    return (
      <>
        <LoadingCard height={110} />
        <LoadingCard height={320} />
      </>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={t("De gegevens konden niet geladen worden.")}
        retryLabel={t("Opnieuw proberen")}
        onRetry={() => setReloadKey((key) => key + 1)}
      />
    );
  }

  if (!resident) {
    return (
      <>
        <button type="button" className="mgr-link" onClick={() => history.push(MANAGER_ROUTES.residents)}>
          ← {t("Bewoners")}
        </button>
        <div className="mgr-card">
          <EmptyState
            title={t("Bewoner niet gevonden")}
            hint={t("Deze bewoner heeft geen bestellingen in de geladen periode.")}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="mgr-link"
        onClick={() => history.push(MANAGER_ROUTES.residents)}
      >
        ← {t("Bewoners")}
      </button>

      {receiptError ? (
        <ErrorState
          message={`${t("De bonnen konden niet gedownload worden.")} (${receiptError})`}
        />
      ) : null}

      <div className="mgr-card mgr-profile">
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {/* L'identifiant tient lieu d'avatar : c'est la référence imprimée
              sur les étiquettes, alors que deux initiales n'identifient rien.
              Il n'est donc plus répété en pastille à côté du nom. */}
          <div className="mgr-profile-id">
            {resident.woshUserId || initialsOf(resident.residentName)}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {isEditing ? (
                <div className="mgr-name-edit">
                  <label>
                    <span>{t("First name")}</span>
                    <input
                      value={nameForm.firstName}
                      onChange={(event) =>
                        setNameForm((form) => ({ ...form, firstName: event.target.value }))
                      }
                      autoFocus
                    />
                  </label>
                  <label>
                    <span>{t("Last Name")}</span>
                    <input
                      value={nameForm.lastName}
                      onChange={(event) =>
                        setNameForm((form) => ({ ...form, lastName: event.target.value }))
                      }
                    />
                  </label>
                </div>
              ) : (
                <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-.5px" }}>
                  {displayName}
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 15.5,
                fontWeight: 600,
                color: "var(--mgr-ink-2)",
                marginTop: 4,
              }}
            >
              {resident.room ? `${t("Kamer")} ${resident.room} · ` : ""}
              {residentOrders.length} {t("bestellingen")} · {formatKilos(totals.kilos)} kg
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {isEditing ? (
            <>
              <button
                type="button"
                className="mgr-btn-ghost"
                onClick={() => setIsEditing(false)}
                disabled={saveState?.status === "saving"}
              >
                {t("Annuleren")}
              </button>
              <button
                type="button"
                className="mgr-btn-primary"
                onClick={saveName}
                disabled={saveState?.status === "saving" || !nameForm.lastName.trim()}
              >
                {saveState?.status === "saving" ? t("Bezig…") : t("Opslaan")}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="mgr-btn-ghost"
              onClick={startEditing}
              disabled={lockedByOrder}
              title={lockedByOrder ? t("Niet mogelijk zolang een bestelling loopt.") : undefined}
            >
              <Icon name="create-outline" style={{ fontSize: 17 }} />
              {t("Naam wijzigen")}
            </button>
          )}
          <button
            type="button"
            className="mgr-btn-ghost"
            onClick={() => downloadReceipts(residentOrders.map((order) => order.id))}
          >
            <Icon name="folder-outline" style={{ fontSize: 17 }} />
            {t("Bonnen downloaden (ZIP)")}
          </button>
          {!isEditing ? (
            <button
              type="button"
              className="mgr-btn-ghost mgr-btn-danger"
              onClick={() => {
                setRemoveState(null);
                setConfirmRemoval(true);
              }}
              disabled={lockedByOrder}
              title={lockedByOrder ? t("Niet mogelijk zolang een bestelling loopt.") : undefined}
            >
              <Icon name="trash-outline" style={{ fontSize: 17 }} />
              {t("Bewoner verwijderen")}
            </button>
          ) : null}
        </div>

        {lockedByOrder ? (
          <div className="mgr-locked">
            <Icon name="alert-circle" style={{ fontSize: 18, flexShrink: 0 }} />
            {t(
              "Naam en verwijdering zijn geblokkeerd zolang een bestelling loopt. Zodra {{reference}} geleverd is, kan het weer.",
              { reference: current?.reference || "" }
            )}
          </div>
        ) : null}
      </div>

      {confirmRemoval ? (
        <div className="mgr-card mgr-card-pad mgr-confirm">
          <div>
            <div className="mgr-note-title">{t("Bewoner verwijderen?")}</div>
            <div style={{ color: "var(--mgr-ink-2)" }}>
              {t(
                "{{name}} verdwijnt uit de lijsten en uit de etikettenkiezer. De bestellingen blijven bewaard.",
                { name: displayName }
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className="mgr-btn-ghost"
              onClick={() => setConfirmRemoval(false)}
              disabled={removeState?.status === "removing"}
            >
              {t("Annuleren")}
            </button>
            <button
              type="button"
              className="mgr-btn-danger-solid"
              onClick={remove}
              disabled={removeState?.status === "removing"}
            >
              {removeState?.status === "removing" ? t("Bezig…") : t("Verwijderen")}
            </button>
          </div>
        </div>
      ) : null}

      {removeState?.status === "error" ? (
        <ErrorState
          message={
            removeState.reason === "locked"
              ? t("Niet mogelijk zolang een bestelling loopt.")

              : removeState.reason === "not-implemented"
              ? t(
                  "De bewoner kon niet verwijderd worden: de server biedt hiervoor nog geen eindpunt aan."
                )
              : `${t("De bewoner kon niet verwijderd worden.")}${
                  removeState.detail ? ` (${removeState.detail})` : ""
                }`
          }
        />
      ) : null}

      {/* Le retour d'action vit dans la carte, sous les champs : en tête de
          page il tombait hors du champ de vision de qui vient de cliquer
          « Enregistrer », et l'action paraissait sans effet. */}
      {saveState?.status === "error" ? (
        <div className="mgr-card mgr-card-pad" style={{ paddingTop: 18, paddingBottom: 18 }}>
          <ErrorState
            message={
              saveState.reason === "locked"
                ? t("Niet mogelijk zolang een bestelling loopt.")

                : saveState.reason === "not-implemented"
                ? t(
                    "De naam kon niet opgeslagen worden: de server biedt hiervoor nog geen eindpunt aan."
                  )
                : `${t("De naam kon niet opgeslagen worden.")}${
                    saveState.detail ? ` (${saveState.detail})` : ""
                  }`
            }
          />
        </div>
      ) : null}

      <div className="mgr-grid-side">
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="mgr-card mgr-card-pad">
            <div className="mgr-card-title" style={{ marginBottom: 14 }}>
              {t("Lopende bestelling")}
            </div>
            {current ? (
              <button
                type="button"
                onClick={() => setOpenOrder(current)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "16px 18px",
                  border: "1.5px solid var(--mgr-border-strong)",
                  borderRadius: 16,
                  width: "100%",
                  background: "#fff",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  flexWrap: "wrap",
                }}
              >
                <StatusPill state={current.state} t={t} size="lg" />
                <span style={{ flex: 1, minWidth: 160 }}>
                  <span style={{ display: "block", fontSize: 16.5, fontWeight: 700 }}>
                    {current.reference} · {t("opgehaald op")} {formatShortDate(current.pickupDate)}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 14.5,
                      fontWeight: 600,
                      color: "var(--mgr-ink-2)",
                      marginTop: 2,
                    }}
                  >
                    {t("terug verwacht op")} {formatShortDate(current.deliveryDate)}
                  </span>
                </span>
                <span style={{ fontSize: 18, fontWeight: 800 }}>
                  {formatCurrency(current.total)}
                </span>
                <span className="mgr-link">{t("Openen")} →</span>
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
                <Icon
                  name="checkmark-circle"
                  style={{ fontSize: 24, color: "var(--mgr-mint)" }}
                />
                <span style={{ fontSize: 15.5, fontWeight: 600, color: "var(--mgr-ink-2)" }}>
                  {t("Geen lopende bestelling.")}
                </span>
              </div>
            )}
          </div>

          <div className="mgr-table">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "20px 24px 14px",
              }}
            >
              <div className="mgr-card-title">{t("Historiek")}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--mgr-ink-2)" }}>
                {formatMonthYear(new Date())}
              </div>
            </div>

            <div className="mgr-thead" style={{ gridTemplateColumns: GRID }}>
              <div>{t("Nr.")}</div>
              <div>{t("Opgehaald")}</div>
              <div>{t("Geleverd")}</div>
              <div>{t("Status")}</div>
              <div className="mgr-num">{t("Kg")}</div>
              <div className="mgr-num">{t("Zakken")}</div>
              <div className="mgr-num">{t("Prijs")}</div>
              <div className="mgr-num">{t("Bon")}</div>
            </div>

            {historyOrders.length === 0 ? (
              <EmptyState title={t("Nog geen afgeronde bestellingen")} />
            ) : (
              historyOrders.map((order) => (
                <div
                  key={order.id}
                  role="button"
                  tabIndex={0}
                  className="mgr-tr"
                  style={{
                    gridTemplateColumns: GRID,
                    borderBottom: "1px solid var(--mgr-hairline)",
                  }}
                  onClick={() => setOpenOrder(order)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setOpenOrder(order);
                    }
                  }}
                >
                  <div style={{ fontWeight: 600, color: "var(--mgr-ink-2)" }}>
                    {order.reference}
                  </div>
                  <div style={{ fontWeight: 600 }}>{formatShortDate(order.pickupDate)}</div>
                  <div style={{ fontWeight: 600 }}>{formatShortDate(order.deliveryDate)}</div>
                  <div>
                    <StatusPill state={order.state} t={t} />
                  </div>
                  <div className="mgr-num" style={{ fontWeight: 700 }}>
                    {order.kilos != null ? formatKilos(order.kilos) : "—"}
                  </div>
                  <div className="mgr-num" style={{ fontWeight: 600 }}>
                    {order.bags != null ? order.bags : "—"}
                  </div>
                  <div className="mgr-num" style={{ fontWeight: 800 }}>
                    {formatCurrency(order.total)}
                  </div>
                  <div className="mgr-num" style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="mgr-link"
                      aria-label={`${t("Bon downloaden")} ${order.reference}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        downloadReceipts([order.id]);
                      }}
                    >
                      <Icon name="download-outline" style={{ fontSize: 19 }} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="mgr-card mgr-card-pad">
            <div className="mgr-card-title" style={{ marginBottom: 16 }}>
              {t("Kosten")}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                padding: "12px 0",
                borderBottom: "1px solid var(--mgr-hairline)",
              }}
            >
              <span style={{ fontSize: 15.5, fontWeight: 600, color: "var(--mgr-ink-2)" }}>
                {formatMonthYear(new Date())}
              </span>
              <span style={{ fontSize: 19, fontWeight: 800 }}>{formatCurrency(totals.month)}</span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                padding: "12px 0",
                borderBottom: "1px solid var(--mgr-hairline)",
              }}
            >
              <span style={{ fontSize: 15.5, fontWeight: 600, color: "var(--mgr-ink-2)" }}>
                {t("Totaal")}
              </span>
              <span style={{ fontSize: 19, fontWeight: 800 }}>{formatCurrency(totals.all)}</span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                padding: "12px 0",
              }}
            >
              <span style={{ fontSize: 15.5, fontWeight: 600, color: "var(--mgr-ink-2)" }}>
                {t("Gemiddeld per bestelling")}
              </span>
              <span style={{ fontSize: 19, fontWeight: 800 }}>
                {formatCurrency(totals.all / Math.max(1, residentOrders.length))}
              </span>
            </div>
            <button
              type="button"
              className="mgr-link"
              style={{ marginTop: 14 }}
              onClick={() => history.push(MANAGER_ROUTES.invoicing)}
            >
              {t("Naar facturatie")} →
            </button>
          </div>
        </div>
      </div>

      {openOrder ? (
        <OrderDrawer
          order={openOrder}
          onClose={() => setOpenOrder(null)}
          onDownloadReceipt={() => downloadReceipts([openOrder.id])}
        />
      ) : null}
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps)
)(Bewonersfiche);
